import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { 
  MapPin, Star, Clock, Phone, Mail, Globe as GlobeIcon, 
  Building, ChevronLeft, Calendar, Users, CheckCircle, 
  X, ArrowRight, Wifi, Coffee, Shield, Car
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  cafe: Coffee,
  showers: Shield,
  lockers: Shield
};

function FacilityDetailPage() {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  
  const [bookingForm, setBookingForm] = useState({
    booking_type: 'hourly',
    start_datetime: '',
    duration_hours: 1,
    sport: '',
    num_people: 1,
    special_requests: ''
  });

  useEffect(() => {
    fetchFacilityDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const fetchFacilityDetails = async () => {
    try {
      setLoading(true);
      const [facilityRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/synergies/facilities/${facilityId}`),
        axios.get(`${API}/synergies/facilities/${facilityId}/reviews`).catch(() => ({ data: [] }))
      ]);
      setFacility(facilityRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching facility details:', error);
      if (error.response?.status === 404) {
        navigate('/synergies/facilities');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    
    if (!token) {
      alert('Please login to book a facility');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/synergies/facilities/${facilityId}/book`,
        bookingForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const calculatePrice = () => {
    if (!facility) return 0;
    
    const rates = {
      hourly: facility.hourly_rate * bookingForm.duration_hours,
      daily: facility.daily_rate,
      weekly: facility.weekly_rate || facility.daily_rate * 7,
      biweekly: facility.biweekly_rate || facility.daily_rate * 14,
      monthly: facility.monthly_rate || facility.daily_rate * 30
    };
    
    return rates[bookingForm.booking_type] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="text-center">
          <Building className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Facility Not Found</h2>
          <Link to="/synergies/facilities" className="text-premium-gold hover:text-premium-gold-light">
            Back to Facilities
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark">
      {/* Header */}
      <div className="bg-dark-surface border-b border-premium-gold/30/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/synergies/facilities"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Facilities</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="h-96 bg-dark-surface rounded-xl overflow-hidden">
            {facility.photos?.[selectedImage] ? (
              <img 
                src={facility.photos[selectedImage]} 
                alt={facility.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-imperial to-dark-purple">
                <Building className="w-24 h-24 text-white/20" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {facility.photos?.slice(0, 4).map((photo, idx) => (
              <div 
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`h-44 bg-dark-surface rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                  selectedImage === idx ? 'border-premium-gold' : 'border-transparent hover:border-premium-gold/30'
                }`}
              >
                <img 
                  src={photo} 
                  alt={`${facility.name} ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Location */}
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {facility.verified && (
                      <span className="px-3 py-1 bg-premium-gold text-dark-purple text-xs font-bold rounded">
                        Verified
                      </span>
                    )}
                    {facility.featured && (
                      <span className="px-3 py-1 bg-premium-gold text-white text-xs font-bold rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <h1 
                    className="text-3xl md:text-4xl font-bold text-white mb-3"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    {facility.name}
                  </h1>
                  <div className="flex items-center gap-4 text-white/60">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" strokeWidth={1.5} />
                      <span>{facility.city}, {facility.country}</span>
                    </div>
                    {facility.rating > 0 && (
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-premium-gold fill-current" strokeWidth={1.5} />
                        <span className="text-white font-semibold">{facility.rating.toFixed(1)}</span>
                        <span>({facility.review_count} reviews)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <p className="text-white/70 leading-relaxed">{facility.description}</p>
              
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-deep-dark text-white/70 text-sm rounded capitalize">
                  {facility.facility_type?.replace(/_/g, ' ')}
                </span>
                <span className="px-3 py-1 bg-deep-dark text-white/70 text-sm rounded capitalize">
                  {facility.indoor_outdoor}
                </span>
                <span className="px-3 py-1 bg-deep-dark text-premium-gold text-sm rounded">
                  {facility.total_bookings} Bookings
                </span>
              </div>
            </div>

            {/* Sports Available */}
            {facility.available_sports?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Sports Available
                </h2>
                <div className="flex flex-wrap gap-2">
                  {facility.available_sports.map((sport, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-deep-dark text-white text-sm rounded capitalize"
                    >
                      {sport}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {facility.amenities?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Amenities
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {facility.amenities.map((amenity, idx) => {
                    const IconComponent = AMENITY_ICONS[amenity.toLowerCase()] || CheckCircle;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-white/70">
                        <IconComponent className="w-5 h-5 text-premium-gold" />
                        <span className="capitalize">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Equipment */}
            {facility.equipment_available?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Equipment Available
                </h2>
                <ul className="grid grid-cols-2 gap-2 text-white/70">
                  {facility.equipment_available.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-premium-gold" />
                      <span className="capitalize">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Operating Hours */}
            {facility.operating_hours && Object.keys(facility.operating_hours).length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Clock className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                  Operating Hours
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(facility.operating_hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between text-white/70">
                      <span className="capitalize font-semibold">{day}:</span>
                      <span>{hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Map */}
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Location
              </h2>
              <p className="text-white/60 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" strokeWidth={1.5} />
                {facility.address}
              </p>
              <div className="h-64 rounded-lg overflow-hidden">
                <MapContainer 
                  center={[facility.latitude, facility.longitude]} 
                  zoom={15} 
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[facility.latitude, facility.longitude]}>
                    <Popup>{facility.name}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Contact Information
              </h2>
              <div className="space-y-3">
                {facility.contact_phone && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Phone className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <a href={`tel:${facility.contact_phone}`} className="hover:text-white transition-colors">
                      {facility.contact_phone}
                    </a>
                  </div>
                )}
                {facility.contact_email && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Mail className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <a href={`mailto:${facility.contact_email}`} className="hover:text-white transition-colors">
                      {facility.contact_email}
                    </a>
                  </div>
                )}
                {facility.website && (
                  <div className="flex items-center gap-3 text-white/70">
                    <GlobeIcon className="w-5 h-5 text-premium-gold" />
                    <a href={facility.website} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50 sticky top-4">
              <h3 className="text-2xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Book This Facility
              </h3>
              
              {/* Pricing */}
              <div className="mb-6 space-y-2">
                <div className="flex justify-between text-white/70">
                  <span>Hourly Rate:</span>
                  <span className="text-white font-bold">₹{facility.hourly_rate}/hr</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>Daily Rate:</span>
                  <span className="text-white font-bold">₹{facility.daily_rate}/day</span>
                </div>
                {facility.weekly_rate && (
                  <div className="flex justify-between text-white/70">
                    <span>Weekly Rate:</span>
                    <span className="text-white font-bold">₹{facility.weekly_rate}/week</span>
                  </div>
                )}
                {facility.monthly_rate && (
                  <div className="flex justify-between text-white/70">
                    <span>Monthly Rate:</span>
                    <span className="text-white font-bold">₹{facility.monthly_rate}/month</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Booking Type</label>
                  <select
                    value={bookingForm.booking_type}
                    onChange={(e) => setBookingForm({ ...bookingForm, booking_type: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    {facility.weekly_rate && <option value="weekly">Weekly</option>}
                    {facility.biweekly_rate && <option value="biweekly">Bi-Weekly</option>}
                    {facility.monthly_rate && <option value="monthly">Monthly</option>}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Start Date & Time</label>
                  <input
                    type="datetime-local"
                    value={bookingForm.start_datetime}
                    onChange={(e) => setBookingForm({ ...bookingForm, start_datetime: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    required
                  />
                </div>

                {bookingForm.booking_type === 'hourly' && (
                  <div>
                    <label className="block text-sm font-semibold text-white/70 mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      min="1"
                      value={bookingForm.duration_hours}
                      onChange={(e) => setBookingForm({ ...bookingForm, duration_hours: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Sport</label>
                  <select
                    value={bookingForm.sport}
                    onChange={(e) => setBookingForm({ ...bookingForm, sport: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    <option value="">Select Sport</option>
                    {facility.available_sports?.map((sport, idx) => (
                      <option key={idx} value={sport}>{sport}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Number of People</label>
                  <input
                    type="number"
                    min="1"
                    value={bookingForm.num_people}
                    onChange={(e) => setBookingForm({ ...bookingForm, num_people: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Special Requests</label>
                  <textarea
                    value={bookingForm.special_requests}
                    onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    rows="3"
                    placeholder="Any special requirements..."
                  />
                </div>

                {/* Total */}
                <div className="pt-4 border-t border-premium-gold/30/50">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-white/70">Total Amount:</span>
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                      ₹{calculatePrice().toLocaleString()}
                    </span>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-premium-gold hover:bg-red-700 text-white font-bold tracking-wider py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Book Now <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </form>

              {/* Cancellation Policy */}
              <div className="mt-6 pt-6 border-t border-premium-gold/30/50">
                <h4 className="text-sm font-bold text-white/70 mb-2 uppercase">Cancellation Policy</h4>
                <p className="text-xs text-white/50">
                  Free cancellation up to {facility.cancellation_hours_before || 24} hours before booking. 
                  After that, {facility.cancellation_fee_percentage || 20}% cancellation fee applies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FacilityDetailPage;
